import { ChangeDetectorRef, Component, OnInit, ViewChild, HostBinding, TemplateRef, OnDestroy } from '@angular/core';
import * as _ from 'underscore';
import { isString } from '../../models//utils';
import { MainEmailThreadDocument } from '../../models/dr-document';
import { DrRequestInfo } from '../../models/dr-request-info';
import { Endpoint, MessageType, UpdateStatusType } from '../../models/enums';
import { GenericColDef, GridContext } from '../../models/grid-context';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { DocumentListComponent } from '../document-list/document-list.component';
import { Subscription } from 'rxjs';
import { View } from '../../models/view';
import { SavedSearch } from '../../models/saved-searches';
import { Comparison } from '../../models/comparison';
import { PagerComponent } from '../pager/pager.component';

@Component({
    standalone: false,
    selector: 'res-email-threads',
    templateUrl: './email-threads.component.html',
    styleUrls: ['./email-threads.component.scss']
})
export class EmailThreadsComponent implements OnInit, OnDestroy {
    @ViewChild('threadGrid', { static: true }) threadGrid: DocumentListComponent;
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('headerTemplate', { static: true }) headerTemplate: TemplateRef<any>;

    private entityChangedEvent: Subscription;
    columnDefs: Array<GenericColDef>;

    generateButtonVisible: boolean;
    emailThreadsActionsDisabled: boolean;
    unthreadEmailsButton: boolean;
    updateThreadsButton: boolean;
    automationId = 'email-threads';

    gridContext = {
        metadata: new GridContext({
            sortAscending: true,
            sortEnabled: false
        })
    };

    gridPagerCustomCallback = (startIndex: any) => {
        if (startIndex !== undefined) {
            this.threadGrid.genericGrid.isBusy = true;
            this.loadData(startIndex);
        }
    }

    responseCallback = (response: any) => {
        if (response) {
            this.emailThreadsActionsDisabled = false;

            if (response.emailThreadsSummary) {
                response.totalCount = response.emailThreadsSummary.threadCount;
            }

            if (response.workingSetView) {
                if (response.workingSetView.attributes) {
                    const att = _.filter(response.workingSetView.attributes, (s) => s.name === 'emailthreadingstate');
                    if (att && att.length > 0) {
                        switch (att[0].value.toLowerCase()) {
                            case 'complete':
                                this.emailThreadsActionsDisabled = false;
                                this.generateButtonVisible = false;
                                this.unthreadEmailsButton = true;
                                break;
                            case 'in_progress':
                                this.generateButtonVisible = false;
                                this.threadGrid.genericGrid.isBusy = true;
                                break;
                            case 'outofdate':
                            case 'out_of_date':
                                this.emailThreadsActionsDisabled = false;
                                this.generateButtonVisible = false;
                                this.updateThreadsButton = true;
                                this.unthreadEmailsButton = true;
                                break;
                            case 'none':
                                this.generateButtonVisible = true;
                                break;
                        }
                    }
                }
            }

            if (response.threads) {
                const threads = (<Array<any>>response.threads).map(t => new MainEmailThreadDocument(t));
                response.threads = threads;
            }
        }
    }

    constructor(public changeDetector: ChangeDetectorRef, private commonService: CommonService, private restService: RestService) {
        this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
            const emailTab = this.commonService.selectedNavigationNode.navigationInfo.tabs.find(tab => tab.name === 'Email Threads');
            if (data === UpdateStatusType.WORKING_SET && emailTab && this.commonService.selectedNavigationNode.navigationInfo.selectedTab.name === 'Email Threads') {
                this.loadData();
            }

        });
    }

    ngOnInit(): void {
        this.threadGrid.isMainEmailThreads = true;
        this.threadGrid.selectedTreeItemChanged(this.commonService.selectedNavigationNode);
        this.loadData();
    }

    loadData(startIndex?): void {
        if (this.threadGrid && this.threadGrid.genericGrid) {
            this.threadGrid.genericGrid.suppressRefresh = true;
        }
        const entity = this.commonService.selectedNavigationNode.data;
        let entityView = this.commonService.selectedNavigationNode.data;
        if (entity.view instanceof View || entity instanceof SavedSearch) {
            entityView = entity.view;
        } else if (entity.workingSetView instanceof View) {
            entityView = entity.workingSetView;
        }


        const requestInfo = {
            workingSetHandle: entityView instanceof Comparison ? entityView.docListHandle : entityView.handle,
            count: 100,
            startIndex: startIndex ? startIndex : 0,
            threadType: 'email'
        };

        this.restService.post(Endpoint.workingSetViewManager_listEmailThreads, requestInfo).then(response => {
            if (response && response.handle) {
                this.listEmailThreads(response, startIndex);
            } else {
                this.threadGrid.genericGrid.isBusy = false;
                this.emailThreadsActionsDisabled = true;
            }
        }, error => {
            if (this.threadGrid.genericGrid) {
                this.threadGrid.genericGrid.isBusy = false;
                this.threadGrid.genericGrid.suppressRefresh = false;
            }
        });
    }

    listEmailThreads(response, startIndex): void {
        this.threadGrid.requestInfo = new DrRequestInfo({
            endpoint: Endpoint.workingSetViewManager_listEmailThreadsResult,
            responseName: 'threads',
            data: { handle: response.handle }
        });
        this.threadGrid.genericGrid.pager.startIndex = startIndex ? startIndex : 0;
        this.threadGrid.genericGrid.pager.endIndex = Math.min(this.threadGrid.genericGrid.pager.startIndex + PagerComponent.itemsOnPage, this.threadGrid.genericGrid.pager.totalItems);
        this.threadGrid.genericGrid.pager.currentPage = (Math.ceil(this.threadGrid.genericGrid.pager.startIndex / 100) + 1);
        if (this.threadGrid.genericGrid) {
            this.threadGrid.genericGrid.suppressRefresh = false;
        }
        this.threadGrid.loadData();
    }

    threadEmails(): void {
        this.generateButtonVisible = false;
        this.threadGrid.genericGrid.isBusy = true;
        this.threadGrid.genericGrid.isBusyMessage = 'Threading emails...';
        this.restService.post(Endpoint.workingSetViewManager_threadEmails, { workingSetViewHandle: this.commonService.projectData.handle, taskDescription: 'Threading Emails' }).then(response => {
            if (isString(response)) {
                this.threadGrid.genericGrid.isBusyMessage = 'Threading emails...' + response + '%...';
            } else if (response && response.handle) {
                this.loadData();
            }
        }, error => {
            this.threadGrid.genericGrid.isBusy = false;
        });
    }

    unthreadAllEmails(): void {
        this.threadGrid.genericGrid.isBusy = true;
        this.threadGrid.genericGrid.isBusyMessage = 'Unthreading emails...';
        this.restService.post(Endpoint.workingSetViewManager_unthreadEmails, { workingSetViewHandle: this.commonService.projectData.handle, taskDescription: 'Unthreading Emails' }).then(response => {
            this.updateThreadsButton = false;
            this.unthreadEmailsButton = false;
            if (isString(response)) {
                this.threadGrid.genericGrid.isBusyMessage = 'Unthreading emails...' + response + '%...';
            } else if (response && response.handle) {
                this.loadData();
            }
        }, error => {
            this.threadGrid.genericGrid.isBusy = false;
            this.entityChangedEvent.unsubscribe();
        });
    }

    ngOnDestroy(): void {
        if (this.entityChangedEvent) {
            this.entityChangedEvent.unsubscribe();
        }
    }
}
